import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/hooks/trpc";
import {
  HarvestProtocol,
  type HarvestProtocolId,
} from "@brewbase/db/types";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useZodForm, Form } from "@/components/form/form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ProtocolListRow } from "@/features/harvest-protocol/protocol-list-row";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";

const newProtocolFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().nullable().optional(),
  version: z.string().nullable().optional(),
});
type NewProtocolFormValues = z.infer<typeof newProtocolFormSchema>;

export const Route = createFileRoute("/harvests/protocols/")({
  component: RouteComponent,
});

function RouteComponent() {
  const utils = trpc.useUtils();
  const [tab, setTab] = useState<"active" | "archived">("active");
  const isArchived = tab === "archived";

  const { data: protocols, isLoading: isLoadingProtocols } =
    trpc.harvest.protocols.list.useQuery(
      isArchived ? { includeArchived: true } : undefined,
    );
  const { data: archivedCount } =
    trpc.harvest.protocols.countArchived.useQuery();

  const invalidate = () => {
    utils.harvest.protocols.list.invalidate();
    utils.harvest.protocols.countArchived.invalidate();
  };

  const reorderMutation = trpc.harvest.protocols.reorder.useMutation({
    onError: (err) => {
      toast.error(`Failed to reorder: ${err.message}`);
      utils.harvest.protocols.list.invalidate();
    },
  });
  const archiveMutation = trpc.harvest.protocols.archive.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Protocol archived");
    },
    onError: (err) => toast.error(err.message),
  });
  const unarchiveMutation = trpc.harvest.protocols.unarchive.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Protocol unarchived");
    },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.harvest.protocols.delete.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Protocol deleted");
    },
    onError: (err) => toast.error(err.message),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (isArchived || !over || active.id === over.id || !protocols) return;

    const oldIndex = protocols.findIndex((p) => p.id.toString() === active.id);
    const newIndex = protocols.findIndex((p) => p.id.toString() === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(protocols, oldIndex, newIndex);
    utils.harvest.protocols.list.setData(undefined, reordered);
    reorderMutation.mutate({
      orderedIds: reordered.map((p) => p.id as HarvestProtocolId),
    });
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-4 flex flex-col grow min-h-0">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold">Harvest Protocols</h1>
        <NewProtocolDialog />
      </div>

      <SlimTabs
        value={tab}
        onValueChange={(v) => setTab(v as "active" | "archived")}
      >
        <SlimTabsList>
          <SlimTabsTrigger value="active">Active</SlimTabsTrigger>
          <SlimTabsTrigger value="archived">
            Archived ({archivedCount ?? 0})
          </SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>

      {isLoadingProtocols || !protocols ? (
        <div>Loading protocols...</div>
      ) : protocols.length === 0 ? (
        <div className="text-sm text-olive-9 py-6">
          {isArchived ? "No archived protocols" : "No protocols yet"}
        </div>
      ) : (
        <div className="border-t border-olive-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={protocols.map((p) => p.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              {protocols.map((p: HarvestProtocol) => (
                <ProtocolListRow
                  key={p.id}
                  protocol={p}
                  archived={isArchived}
                  onArchive={() =>
                    archiveMutation.mutate(p.id as HarvestProtocolId)
                  }
                  onUnarchive={() =>
                    unarchiveMutation.mutate(p.id as HarvestProtocolId)
                  }
                  onDelete={() =>
                    deleteMutation.mutate(p.id as HarvestProtocolId)
                  }
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

function NewProtocolDialog() {
  const [isOpen, _setIsOpen] = useState(false);
  const navigate = Route.useNavigate();
  const utils = trpc.useUtils();

  const form = useZodForm({
    schema: newProtocolFormSchema,
    defaultValues: {
      name: "",
      description: "",
      version: "1.0.0",
    },
  });

  const setIsOpen = (isOpen: boolean) => {
    form.reset();
    _setIsOpen(isOpen);
  };

  const createProtocolMutation = trpc.harvest.protocols.create.useMutation({
    onSuccess: (data: HarvestProtocol & { id: HarvestProtocolId }) => {
      toast.success(`Protocol "${data.name}" created successfully!`);
      utils.harvest.protocols.list.invalidate();
      setIsOpen(false);
      navigate({ to: `/harvests/protocols/${data.id}` });
    },
    onError: (error: { message: string }) => {
      toast.error(`Failed to create protocol: ${error.message}`);
    },
  });

  const onSubmit = (values: NewProtocolFormValues) => {
    const payload = {
      ...values,
      version: values.version || "1.0.0",
    };
    createProtocolMutation.mutate(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">New Harvest Protocol</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>New Harvest Protocol</DialogTitle>
        </DialogHeader>
        <Form form={form} onSubmit={onSubmit} submitText="Create Protocol">
          <div className="space-y-4 py-2 pb-4">
            <div className="space-y-1">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...form.register("name")}
                placeholder="e.g., Bench"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...form.register("description")}
                placeholder="Describe the purpose and scope of this protocol."
              />
              {form.formState.errors.description && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                {...form.register("version")}
                placeholder="e.g., 1.0.0"
              />
              {form.formState.errors.version && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.version.message}
                </p>
              )}
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
